import { DatePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmHelperService, FirstCapitalPipe, SvgIconsComponent } from '@zayan-erp/shared';
import { FileSystemSharing, SharedWithGroup, SharedWithUser } from '../../models/file-sharing.models';
import { FilesystemService } from '../../services/file-system.service';

@Component({
  selector: 'app-shared-with-modal',
  standalone: true,
  imports: [DatePipe, NgClass, NgTemplateOutlet, FirstCapitalPipe, SvgIconsComponent],
  templateUrl: './shared-with-modal.component.html',
  styleUrl: './shared-with-modal.component.scss'
})
export class SharedWithModalComponent implements OnInit {
  @Input() fileId = '';

  @Output() accessRemoved = new EventEmitter<void>();

  activeModal = inject(NgbActiveModal);
  #filesystemService = inject(FilesystemService);
  #confirmHelperService = inject(ConfirmHelperService);

  isLoading = signal(false);
  activeSharedTab = signal(true);
  previousSharedUser = signal<SharedWithUser[]>([]);
  previousSharedGroup = signal<SharedWithGroup[]>([]);
  ariaExpanded = signal<boolean[]>([]);

  ngOnInit(): void {
    this.#loadSharedWith();
  }

  selectTab(isUsers: boolean): void {
    this.activeSharedTab.set(isUsers);
  }

  toggleAriaExpanded(index: number): void {
    this.ariaExpanded.update(expanded => {
      const next = [...expanded];
      next[index] = !next[index];
      return next;
    });
  }

  confirmRemoveAccess(item: SharedWithUser | SharedWithGroup): void {
    this.#confirmHelperService
      .showModal({
        icon: 'confirm-icon',
        header: 'Delete Confirmation?',
        message: 'Are you sure that you want to delete these items?',
        acceptLabel: 'Delete',
        acceptSeverity: 'danger',
        rejectLabel: 'Cancel'
      })
      .subscribe(confirmed => {
        if (confirmed) {
          this.#removeAccess(item);
        }
      });
  }

  #removeAccess(item: SharedWithUser | SharedWithGroup): void {
    const groupId = item.sharedWithGroup?.id ?? null;
    const request$ =
      groupId !== null
        ? this.#filesystemService.removeGroupPermission(item.fileSystemId ?? this.fileId, groupId)
        : this.#filesystemService.removeUserPermission(item.id);

    request$.subscribe({
      next: () => {
        this.accessRemoved.emit();
        this.#loadSharedWith();
      }
    });
  }

  #loadSharedWith(): void {
    this.isLoading.set(true);
    this.#filesystemService.getSharedWith(this.fileId).subscribe({
      next: (data: FileSystemSharing) => {
        this.previousSharedUser.set(data?.sharedWithUsers ?? []);
        this.previousSharedGroup.set(data?.sharedWithGroups ?? []);
        this.ariaExpanded.set([]);
        this.isLoading.set(false);
      },
      error: () => this.isLoading.set(false)
    });
  }
}
